import { isAgentFeatureAvailable } from "@/configs/agentFeatures";
import { INavigation } from "@/configs/menu";
import { AccountTypes } from "@/configs/services";
import { AuthorityKey } from "@/context/UserContext";
import { isDemo } from "@/libs/utils";
import { ConfigParameters, IAccount, IUser } from "@/types/api";

interface NavigationAccessContext {
  accounts: IAccount[];
  hasAuthority: (name: AuthorityKey) => boolean;
  userState: IUser;
  configParameters: ConfigParameters;
  /** Route access, not sidebar display: `hiddenByFeatureConfig` only hides the link. */
  forRoute?: boolean;
}

const hasNavigationAccess = (
  item: INavigation,
  {
    accounts,
    hasAuthority,
    userState,
    configParameters,
    forRoute = false,
  }: NavigationAccessContext,
) => {
  const user = userState.user;

  if (
    item.authorities?.length &&
    !item.authorities.some(authority => hasAuthority(authority))
  ) {
    return false;
  }

  if (item.featureConfig && !configParameters[item.featureConfig]) {
    return false;
  }

  if (
    item.agentFeature &&
    !isAgentFeatureAvailable(item.agentFeature, configParameters)
  ) {
    return false;
  }

  if (
    !forRoute &&
    item.hiddenByFeatureConfig &&
    configParameters[item.hiddenByFeatureConfig]
  ) {
    return false;
  }

  if (item.onlyForDemo && !isDemo()) {
    return false;
  }

  if (item.notForTrialTenant && configParameters.isTrialTenant) {
    return false;
  }

  if (item.whitelist && !user?.whiteLists?.[item.whitelist]) {
    return false;
  }

  if (
    item.allowedUserTypes?.length &&
    (!user?.userType || !item.allowedUserTypes.includes(user.userType))
  ) {
    return false;
  }

  // Entries that only make sense for tenants holding a given account type.
  // `INavigation.accounts` declares it, rather than the gate matching on a
  // specific item key.
  if (
    item.accounts?.length &&
    !accounts.some(account =>
      item.accounts?.includes(account.type as AccountTypes),
    )
  ) {
    return false;
  }

  return true;
};

/**
 * Resolve one navigation branch without mutating the shared module-level
 * configuration. The sidebar and Administration pages both consume this
 * result, so one consumer cannot leak stale `display` state to another.
 */
export const computeNavigationItem = <T extends INavigation>(
  item: T,
  accounts: IAccount[],
  hasAuthority: (name: AuthorityKey) => boolean,
  userState: IUser,
  configParameters: ConfigParameters,
  forRoute = false,
): T => {
  const children = item.children?.map(child =>
    computeNavigationItem(
      child,
      accounts,
      hasAuthority,
      userState,
      configParameters,
      forRoute,
    ),
  );
  const hasVisibleChild = children?.some(child => child.display !== false);
  const display =
    hasNavigationAccess(item, {
      accounts,
      hasAuthority,
      userState,
      configParameters,
      forRoute,
    }) &&
    (!item.hideWhenNoVisibleChildren || Boolean(hasVisibleChild));

  return {
    ...item,
    display,
    children,
  };
};

export const flattenNavigations = (navigations: INavigation[]): INavigation[] =>
  navigations.flatMap(item => [
    item,
    ...(item.children ? flattenNavigations(item.children) : []),
  ]);

export interface CompiledRoutePattern<T extends INavigation = INavigation> {
  entry: T;
  regex: RegExp;
  staticBase: string;
}

/** Exact-match regex for a route pattern - `:param` matches any non-slash run. */
export const compileRouteRegex = (pattern: string): RegExp =>
  new RegExp(`^${pattern.replace(/:[^/]+/g, "[^/]+")}$`);

/**
 * Pair each routable entry with its regex and `staticBase` - the leading
 * literal segment, used to decide which deeper paths it owns.
 */
export const compileNavigationRoutes = <T extends INavigation>(
  entries: T[],
): CompiledRoutePattern<T>[] =>
  entries
    .filter(entry => Boolean(entry.route))
    .map(entry => ({
      entry,
      regex: compileRouteRegex(entry.route),
      staticBase: (entry.activeBasePath ?? entry.route).split("/:")[0],
    }));

/**
 * The single "which navigation entry owns this pathname" rule, shared by the
 * route loader and the sidebar so the two cannot drift.
 *
 * A container's route is a prefix of its children's routes, so the longest
 * matching static base wins - otherwise `/app/admin` would beat the capability
 * the user is actually on.
 *
 * `containersOwnDescendants` is the one place the two callers differ, and it
 * is deliberate. The sidebar wants it ON: standing on any `/app/admin/**` page
 * should highlight Administration even when no entry claims that exact path.
 * The loader wants it OFF: letting a container own paths it has no entry for
 * would gate unrelated pages behind the container's own visibility.
 */
export const findOwningNavigation = <T extends INavigation>(
  pathname: string,
  compiled: CompiledRoutePattern<T>[],
  { containersOwnDescendants = false } = {},
): T | undefined => {
  let owner: T | undefined;
  let bestLength = -1;

  for (const { entry, regex, staticBase } of compiled) {
    const ownsDeeper = containersOwnDescendants || !entry.children?.length;
    const matches =
      regex.test(pathname) ||
      (ownsDeeper && pathname.startsWith(`${staticBase}/`));

    if (matches && staticBase.length > bestLength) {
      owner = entry;
      bestLength = staticBase.length;
    }
  }

  return owner;
};
